import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import type { I18nMap } from "./TranslationsPanel";
import {
  isSupabaseConnected,
  fetchAllJournalEntries,
  insertJournalEntry,
  updateJournalEntry as updateJournalEntryApi,
  deleteJournalEntry as deleteJournalEntryApi,
  fetchWorkshops,
  insertWorkshop,
  updateWorkshop as updateWorkshopApi,
  deleteWorkshop as deleteWorkshopApi,
  fetchProducts,
  insertProduct,
  updateProduct as updateProductApi,
  deleteProduct as deleteProductApi,
  fetchSiteConfig,
  flatToSiteConfig,
  fetchAllReviews,
  updateReview as updateReviewApi,
  deleteReview as deleteReviewApi,
  fetchPromoCodes,
  insertPromoCode,
  updatePromoCode as updatePromoCodeApi,
  deletePromoCode as deletePromoCodeApi,
  fetchOrders,
  updateOrderStatus as updateOrderStatusApi,
  updateOrderStatusBulk as updateOrderStatusBulkApi,
  hideOrders as hideOrdersApi,
} from "../supabase";

// ─── Types ───
export interface AdminProduct {
  id: number;
  slug: string;
  name: string;
  price: number;
  category: string;
  tag?: "new" | "limited" | "sold out" | "";
  description: string;
  details: { label: string; value: string }[];
  images: string[];
  relatedIds: number[];
  stock_quantity: number;
  low_stock_threshold: number;
  i18n?: I18nMap;
}

export interface AdminOrder {
  id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_country?: string;
  delivery: "pickup" | "shipping";
  shipping_address: string;
  notes: string;
  subtotal: number;
  status: "pending" | "confirmed" | "shipped" | "completed" | "cancelled";
  items: { product_id: number; product_name: string; quantity: number; unit_price: number }[];
  created_at: string;
}

export interface AdminWorkshop {
  id: number;
  title: string;
  date: string;
  location: string;
  image: string;
  description?: string;
  i18n?: I18nMap;
}

export interface SiteConfig {
  logo?: string; // data URL or external URL for brand logo (SVG/PNG/JPG)
  hero: {
    title: string;
    subtitle: string;
    cta_text: string;
    image: string;
    i18n?: I18nMap;
  };
  about: {
    label: string;
    title: string;
    paragraph1: string;
    paragraph2: string;
    image: string;
    cta_text: string;
    i18n?: I18nMap;
  };
  features?: {
    reviews: boolean;
    newsletter: boolean;
    promoCodes: boolean;
    faq: boolean;
    wishlist: boolean;
    account: boolean;
  };
  theme?: {
    colors: {
      background: string;
      foreground: string;
      primary: string;
      accent: string;
      muted: string;
      border: string;
      card: string;
    };
    navbar: {
      background: string;
      scrolledBackground: string;
      textColor: string;
    };
    buttons: {
      primaryBg: string;
      primaryText: string;
      borderRadius: "full" | "lg" | "md" | "sm" | "none";
    };
    fonts: {
      heading: string;
      body: string;
    };
  };
  faqItems?: FAQItem[];
  socialLinks?: {
    instagram?: string;
    tiktok?: string;
    twitter?: string;
    facebook?: string;
    youtube?: string;
    pinterest?: string;
    linkedin?: string;
  };
  slogan?: string;
  sloganI18n?: I18nMap;
  /** Footer contact (below logo). Set in admin Site config > Brand. */
  contact_email?: string;
  contact_phone?: string;
  contact_address?: string;
  wholesale?: WholesaleConfig;
}

export interface WholesaleConfig {
  hero: {
    label: string;
    title: string;
    subtitle: string;
    image: string;
    i18n?: I18nMap;
  };
  description: {
    label: string;
    title: string;
    text: string;
    i18n?: I18nMap;
  };
  showcase: {
    label: string;
    title: string;
    features: string[];
    image: string;
    cta_text: string;
    i18n?: I18nMap;
  };
  tiers: WholesaleTier[];
  faqs: { q: string; a: string; i18n?: I18nMap }[];
  inquiry: {
    label: string;
    title: string;
    subtitle: string;
    i18n?: I18nMap;
  };
  certifications: { title: string; desc: string; i18n?: I18nMap }[];
}

export interface WholesaleTier {
  name: string;
  minOrder: string;
  priceRange: string;
  features: string[];
  featured?: boolean;
  i18n?: I18nMap;
}

export interface FAQItem {
  id: string;
  q: { en: string; ja?: string; nl?: string; fr?: string };
  a: { en: string; ja?: string; nl?: string; fr?: string };
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: "admin" | "editor" | "journal_editor";
  created_at: string;
  last_login?: string;
}

export interface JournalEntry {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  image: string;
  author: string;
  published: boolean;
  created_at: string;
  i18n?: I18nMap;
}

export interface Review {
  id: string;
  product_id: number;
  name: string;
  rating: number;
  comment: string;
  approved: boolean;
  created_at: string;
}

export interface PromoCode {
  id: string;
  code: string;
  discount_type: "percentage" | "fixed";
  discount_value: number;
  active: boolean;
  expires_at?: string;
}

// ─── Default data ───
const defaultProducts: AdminProduct[] = [
  {
    id: 1,
    slug: "ceremonial-matcha",
    name: "Ceremonial Matcha",
    price: 42,
    category: "Matcha",
    tag: "new",
    description: "Our signature ceremonial grade matcha, stone-ground from first-harvest tencha leaves grown in the shaded hills of Uji, Kyoto. Vibrant emerald color with a rich umami profile, silky texture, and a naturally sweet finish. Perfect for traditional usucha preparation.",
    details: [
      { label: "Origin", value: "Uji, Kyoto, Japan" },
      { label: "Harvest", value: "First harvest (Ichibancha)" },
      { label: "Grade", value: "Ceremonial" },
      { label: "Weight", value: "40g tin" },
      { label: "Shading", value: "21+ days" },
      { label: "Grinding", value: "Traditional granite stone mill" },
    ],
    images: [
      "https://images.unsplash.com/photo-1753009712810-3f72c3f72548?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjZXJlbW9uaWFsJTIwbWF0Y2hhJTIwcG93ZGVyJTIwZ3JlZW58ZW58MXx8fHwxNzcyOTA5ODUxfDA&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1754606581556-f1ce15df35b8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjBncmVlbiUyMHRlYSUyMHBvd2RlciUyMGNsb3NlJTIwdXB8ZW58MXx8fHwxNzcyOTExNjA5fDA&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1752351354291-3d2ce93a17e3?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjBwb3dkZXIlMjBzaWZ0ZWQlMjBmaW5lJTIwZ3JlZW58ZW58MXx8fHwxNzcyOTExNjE0fDA&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1734104741017-6c2cb19bff3f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjB0ZWElMjB0aW4lMjBjb250YWluZXIlMjBwYWNrYWdpbmd8ZW58MXx8fHwxNzcyOTExNjEzfDA&ixlib=rb-4.1.0&q=80&w=1080",
    ],
    relatedIds: [3, 4],
    stock_quantity: 24,
    low_stock_threshold: 5,
  },
  {
    id: 2,
    slug: "handmade-chawan-bowl",
    name: "Handmade Chawan Bowl",
    price: 78,
    category: "Ceramics",
    tag: "limited",
    description: "A one-of-a-kind chawan (tea bowl) handcrafted by artisan potter Kenji Yamamoto in Shigaraki, Japan. Each bowl is wheel-thrown, bisque-fired, and finished with a wabi-sabi ash glaze that creates unique, unrepeatable patterns.",
    details: [
      { label: "Artisan", value: "Kenji Yamamoto, Shigaraki" },
      { label: "Material", value: "Stoneware clay" },
      { label: "Glaze", value: "Natural wood ash" },
      { label: "Dimensions", value: "12cm dia \u00d7 8cm h" },
      { label: "Capacity", value: "~300ml" },
      { label: "Care", value: "Hand wash, microwave safe" },
    ],
    images: [
      "https://images.unsplash.com/photo-1766399654235-a6793895422d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxoYW5kbWFkZSUyMGNlcmFtaWMlMjBjaGF3YW4lMjBib3dsfGVufDF8fHx8MTc3MjkwOTg1Mnww&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1768677675311-ad831e8d4be0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxqYXBhbmVzZSUyMGNlcmFtaWNlJTIwZGVzaWduJTIwZ2xhemUlMjB0ZWElMjBib3dsJTIwd2FiaSUyMHNhYml8ZW58MXx8fHwxNzcyOTExNjEwfDA&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1663888672547-360e763e4ac7?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjZXJhbWljJTIwcG90dGVyeSUyMGhhbmRtYWRlJTIwZWFydGglMjB0b25lfGVufDF8fHx8MTc3MjkxMTYxMnww&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1762876244984-f9d493cf4aa1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0ZWElMjBjZXJlbW9ueSUyMHplbiUyMGNhbG0lMjBtaW5pbWFsaXN0fGVufDF8fHx8MTc3MjkxMTYxNHww&ixlib=rb-4.1.0&q=80&w=1080",
    ],
    relatedIds: [3, 1],
    stock_quantity: 8,
    low_stock_threshold: 3,
  },
  {
    id: 3,
    slug: "bamboo-whisk-chasen",
    name: "Bamboo Whisk (Chasen)",
    price: 34,
    category: "Tea Tools",
    description: "A hand-carved chasen made from a single piece of aged bamboo by a fourth-generation whisk maker in Takayama, Nara. 80 fine tines create the perfect froth for ceremonial matcha.",
    details: [
      { label: "Origin", value: "Takayama, Nara, Japan" },
      { label: "Material", value: "White bamboo (Hachiku)" },
      { label: "Tines", value: "80 prongs (Hachijuppondate)" },
      { label: "Includes", value: "Ceramic kusenaoshi holder" },
      { label: "Use", value: "Usucha & Koicha" },
      { label: "Care", value: "Rinse with warm water, air dry" },
    ],
    images: [
      "https://images.unsplash.com/photo-1755685682321-d4a38aa26214?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiYW1ib28lMjB3aGlzayUyMGNoYXNlbiUyMG1hdGNoYXxlbnwxfHx8fDE3NzI5MDk4NTJ8MA&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1636396279211-25e3a26b2621?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiYW1ib28lMjB0ZWElMjB3aGlzayUyMG1hdGNoYSUyMHRvb2xzfGVufDF8fHx8MTc3MjkxMTYxMXww&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1767840272143-f76925e69ccd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxqYXBhbmVzZSUyMHRlYSUyMGNlcmVtb255JTIwcG91cmluZ3xlbnwxfHx8fDE3NzI5MTE2MTJ8MA&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1762876244984-f9d493cf4aa1?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0ZWElMjBjZXJlbW9ueSUyMHplbiUyMGNhbG0lMjBtaW5pbWFsaXN0fGVufDF8fHx8MTc3MjkxMTYxNHww&ixlib=rb-4.1.0&q=80&w=1080",
    ],
    relatedIds: [2, 4],
    stock_quantity: 15,
    low_stock_threshold: 5,
  },
  {
    id: 4,
    slug: "matcha-starter-set",
    name: "Matcha Starter Set",
    price: 120,
    category: "Gift Sets",
    tag: "new",
    description: "Everything you need to begin your matcha journey. This curated set includes our signature Ceremonial Matcha (40g), a handmade Chawan bowl, a Chasen bamboo whisk, a Chashaku bamboo scoop, and a step-by-step preparation guide.",
    details: [
      { label: "Contents", value: "Matcha, Chawan, Chasen, Chashaku, Guide" },
      { label: "Matcha", value: "Ceremonial grade, 40g tin" },
      { label: "Bowl", value: "Handmade stoneware" },
      { label: "Packaging", value: "Linen gift box" },
      { label: "Perfect for", value: "Beginners & gifting" },
      { label: "Value", value: "Save \u20ac34 vs. buying separately" },
    ],
    images: [
      "https://images.unsplash.com/photo-1657533038120-acfa2ef77c12?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjB0ZWElMjBnaWZ0JTIwc2V0JTIwbWluaW1hbHxlbnwxfHx8fDE3NzI5MDk4NTN8MA&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1759420376616-38ab47156c72?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxqYXBhbmVzZSUyMHRlYSUyMHNldCUyMGdpZnQlMjBib3glMjBtaW5pbWFsfGVufDF8fHx8MTc3MjkxMTYxMXww&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1512484149279-9751676e56c4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjBsYXR0ZSUyMGdyZWVuJTIwZHJpbmslMjBhcnR8ZW58MXx8fHwxNzcyOTExNjEyfDA&ixlib=rb-4.1.0&q=80&w=1080",
      "https://images.unsplash.com/photo-1734104741017-6c2cb19bff3f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjB0ZWElMjB0aW4lMjBjb250YWluZXIlMjBwYWNrYWdpbmd8ZW58MXx8fHwxNzcyOTExNjEzfDA&ixlib=rb-4.1.0&q=80&w=1080",
    ],
    relatedIds: [1, 2, 3],
    stock_quantity: 4,
    low_stock_threshold: 5,
  },
];

const defaultWorkshops: AdminWorkshop[] = [
  {
    id: 1,
    title: "Introduction to Chanoyu",
    date: "April 12, 2026",
    location: "Kyoto Studio, Tokyo",
    image: "https://images.unsplash.com/photo-1614447911900-a69203b0e6b6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx6ZW4lMjB0ZWElMjBob3VzZSUyMGNhbG0lMjBpbnRlcmlvcnxlbnwxfHx8fDE3NzI5MDk4NTR8MA&ixlib=rb-4.1.0&q=80&w=1080",
    description: "A gentle introduction to the Japanese tea ceremony tradition.",
  },
  {
    id: 2,
    title: "Ceramics & Tea Making",
    date: "May 3, 2026",
    location: "Artisan Workshop, Osaka",
    image: "https://images.unsplash.com/photo-1695746999130-17bc94e000e8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxqYXBhbmVzZSUyMGNlcmFtaWNzJTIwd29ya3Nob3AlMjBwb3R0ZXJ5fGVufDF8fHx8MTc3MjkwOTg1NHww&ixlib=rb-4.1.0&q=80&w=1080",
    description: "Learn to craft your own chawan alongside master potters.",
  },
  {
    id: 3,
    title: "Mindful Matcha Morning",
    date: "May 24, 2026",
    location: "Zen Garden, Kyoto",
    image: "https://images.unsplash.com/photo-1750104141221-15184162bfe9?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx0ZWElMjBjZXJlbW9ueSUyMG1pbmRmdWxuZXNzJTIwcGVhY2VmdWx8ZW58MXx8fHwxNzcyOTA5ODU1fDA&ixlib=rb-4.1.0&q=80&w=1080",
    description: "Start your morning with meditation and mindful matcha preparation.",
  },
];

const defaultSiteConfig: SiteConfig = {
  logo: undefined,  // falls back to the default MoechaLogo.svg in Navigation/Footer
  hero: {
    title: "The Ritual of Matcha",
    subtitle: "Slow down. Sip mindfully.",
    cta_text: "Discover Matcha",
    image: "https://images.unsplash.com/photo-1648889095175-1757165415e5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjB0ZWElMjBwcmVwYXJhdGlvbiUyMGNlcmFtaWMlMjBib3dsfGVufDF8fHx8MTc3MjkwOTg1MXww&ixlib=rb-4.1.0&q=80&w=1080",
  },
  about: {
    label: "The Philosophy",
    title: "The Art of the Tea Ritual",
    paragraph1: "In the quiet stillness of a tea room, time slows to the rhythm of water meeting leaf. The Japanese tea ceremony \u2014 chanoyu \u2014 is not merely about drinking tea. It is a meditation in motion, a practice of mindfulness passed down through centuries.",
    paragraph2: "Each gesture is intentional: the warming of the bowl, the gentle whisking, the moment of pause before the first sip. We bring this same reverence to every product we craft \u2014 honoring tradition, embracing imperfection, and finding beauty in simplicity.",
    image: "https://images.unsplash.com/photo-1765035058192-edd7ee3e0df0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxqYXBhbmVzZSUyMHRlYSUyMGNlcmVtb255JTIwcml0dWFsfGVufDF8fHx8MTc3MjkwOTg1M3ww&ixlib=rb-4.1.0&q=80&w=1080",
    cta_text: "Learn More",
  },
  features: {
    reviews: true,
    newsletter: false,
    promoCodes: true,
    faq: true,
    wishlist: true,
    account: false,
  },
  theme: {
    colors: {
      background: "#F7F5F1",
      foreground: "#2D2A26",
      primary: "#6F7F63",
      accent: "#C7A76C",
      muted: "#6F675F",
      border: "#E6E0D7",
      card: "#FFFFFF",
    },
    navbar: {
      background: "#F7F5F1",
      scrolledBackground: "rgba(247,245,241,0.95)",
      textColor: "#2D2A26",
    },
    buttons: {
      primaryBg: "#6F7F63",
      primaryText: "#FFFFFF",
      borderRadius: "full",
    },
    fonts: {
      heading: "Cormorant Garamond",
      body: "Jost",
    },
  },
  faqItems: [
    {
      id: "faq_001",
      q: {
        en: "What is the difference between ceremonial and culinary grade matcha?",
        ja: "薄茶用と料理用の抹茶の違いは何ですか？",
        nl: "Wat is het verschil tussen ceremoniële en culinaire matcha?",
        fr: "Quelle est la différence entre le matcha cérémoniel et culinaire ?",
      },
      a: {
        en: "Ceremonial grade matcha is made from the youngest, most tender leaves and is stone-ground to an ultra-fine powder. It has a smooth, umami-rich flavor ideal for drinking straight with water. Culinary grade matcha uses slightly older leaves, resulting in a bolder flavor profile that works beautifully in lattes, smoothies, and baking.",
        ja: "薄茶用抹茶は最も若く柔らかい葉から作られ、石臼で超微粉に挽かれます。滑らかで旨味豊かな風味があり、水で直接飲むのに最適です。料理用抹茶はやや成熟した葉を使用し、ラテ、スムージー、お菓子作りに適した力強い風味です。",
        nl: "Ceremoniële matcha wordt gemaakt van de jongste, teerste bladeren en is steengemalen tot een ultrafijn poeder. Het heeft een gladde, umami-rijke smaak die ideaal is om puur met water te drinken. Culinaire matcha gebruikt iets oudere bladeren, wat resulteert in een krachtigere smaak die perfect werkt in lattes, smoothies en bakken.",
        fr: "Le matcha cérémoniel est fabriqué à partir des feuilles les plus jeunes et les plus tendres, moulues à la pierre en une poudre ultra-fine. Il a une saveur douce et riche en umami, idéale pour être bu pur avec de l'eau. Le matcha culinaire utilise des feuilles légèrement plus mûres, offrant un profil de saveur plus prononcé, parfait pour les lattés, smoothies et pâtisseries.",
      },
    },
    {
      id: "faq_002",
      q: {
        en: "How should I prepare matcha?",
        ja: "抹茶の淹れ方は？",
        nl: "Hoe bereid ik matcha?",
        fr: "Comment préparer le matcha ?",
      },
      a: {
        en: "Sift 1-2 grams (about 1 chashaku scoop) of matcha into a warm chawan. Add 70ml of water heated to 70-80°C (not boiling). Whisk vigorously with your chasen in a W or M motion until a fine, velvety froth forms. The entire ritual should take about 30 seconds.",
        ja: "1〜2グラム（茶杓1杯分）の抹茶を温めた茶碗にふるい入れます。70〜80℃のお湯を70ml注ぎます（沸騰させないでください）。茶筅でWまたはMの字を描くように素早く点て、きめ細かな泡が立つまで約30秒続けます。",
        nl: "Zeef 1-2 gram (ongeveer 1 chashaku schep) matcha in een warme chawan. Voeg 70 ml water toe dat is verwarmd tot 70-80°C (niet kokend). Klop krachtig met je chasen in een W- of M-beweging totdat een fijn, fluwelig schuim ontstaat. Het hele ritueel duurt ongeveer 30 seconden.",
        fr: "Tamisez 1 à 2 grammes (environ 1 cuillère chashaku) de matcha dans un chawan tiède. Ajoutez 70 ml d'eau chauffée à 70-80°C (pas bouillante). Fouettez vigoureusement avec votre chasen en mouvement W ou M jusqu'à obtenir une mousse fine et veloutée. Le rituel entier dure environ 30 secondes.",
      },
    },
    {
      id: "faq_003",
      q: {
        en: "How should I store my matcha?",
        ja: "抹茶の保存方法は？",
        nl: "Hoe bewaar ik mijn matcha?",
        fr: "Comment conserver mon matcha ?",
      },
      a: {
        en: "Store matcha in an airtight container away from light, heat, and moisture. For optimal freshness, keep it refrigerated after opening and consume within 4-6 weeks. Our tins are designed to protect against light and air, but we recommend transferring to a smaller container as you use it.",
        ja: "抹茶は光、熱、湿気を避けて密封容器に保存してください。開封後は冷蔵庫で保管し、4〜6週間以内にお使いください。当店の缶は光と空気から保護するよう設計されていますが、使用するにつれて小さな容器に移し替えることをお勧めします。",
        nl: "Bewaar matcha in een luchtdichte container, beschermd tegen licht, warmte en vocht. Bewaar het na opening in de koelkast voor optimale versheid en gebruik het binnen 4-6 weken. Onze blikken zijn ontworpen om te beschermen tegen licht en lucht, maar we raden aan om het over te gieten in een kleinere container naarmate je het gebruikt.",
        fr: "Conservez le matcha dans un récipient hermétique à l'abri de la lumière, de la chaleur et de l'humidité. Pour une fraîcheur optimale, réfrigérez-le après ouverture et consommez-le dans les 4 à 6 semaines. Nos boîtes sont conçues pour protéger de la lumière et de l'air, mais nous recommandons de le transvaser dans un plus petit récipient au fur et à mesure.",
      },
    },
    {
      id: "faq_004",
      q: {
        en: "What are the health benefits of matcha?",
        ja: "抹茶の健康効果は？",
        nl: "Wat zijn de gezondheidsvoordelen van matcha?",
        fr: "Quels sont les bienfaits du matcha pour la santé ?",
      },
      a: {
        en: "Matcha is rich in antioxidants (particularly catechins), L-theanine for calm focus, and chlorophyll. Because you consume the entire leaf, you receive significantly more nutrients than traditional steeped green tea. Matcha provides sustained energy without the jitters associated with coffee.",
        ja: "抹茶は抗酸化物質（特にカテキン）、穏やかな集中力をもたらすL-テアニン、クロロフィルが豊富です。茶葉全体を摂取するため、通常の緑茶よりもはるかに多くの栄養素を得ることができます。コーヒーのような不快な興奮なく、持続的なエネルギーを提供します。",
        nl: "Matcha is rijk aan antioxidanten (met name catechines), L-theanine voor kalme focus en chlorofyl. Omdat je het hele blad consumeert, krijg je aanzienlijk meer voedingsstoffen dan bij traditioneel getrokken groene thee. Matcha geeft langdurige energie zonder de nervositeit van koffie.",
        fr: "Le matcha est riche en antioxydants (notamment les catéchines), en L-théanine pour une concentration calme, et en chlorophylle. Comme vous consommez la feuille entière, vous recevez beaucoup plus de nutriments que le thé vert infusé traditionnel. Le matcha fournit une énergie durable sans la nervosité associée au café.",
      },
    },
    {
      id: "faq_005",
      q: {
        en: "Do you offer international shipping?",
        ja: "海外配送は可能ですか？",
        nl: "Bieden jullie internationale verzending aan?",
        fr: "Proposez-vous la livraison internationale ?",
      },
      a: {
        en: "Yes, we ship worldwide. Orders within Japan arrive within 2-3 business days. International orders typically take 5-10 business days depending on your location. All matcha is shipped in temperature-controlled packaging to ensure freshness.",
        ja: "はい、世界中に配送しています。日本国内は2〜3営業日でお届けします。海外への配送は、地域により5〜10営業日かかります。すべての抹茶は鮮度を保つため温度管理された梱包で出荷されます。",
        nl: "Ja, we verzenden wereldwijd. Bestellingen binnen Japan arriveren binnen 2-3 werkdagen. Internationale bestellingen duren doorgaans 5-10 werkdagen, afhankelijk van je locatie. Alle matcha wordt verzonden in temperatuurgecontroleerde verpakking om de versheid te garanderen.",
        fr: "Oui, nous livrons dans le monde entier. Les commandes au Japon arrivent sous 2-3 jours ouvrables. Les commandes internationales prennent généralement 5-10 jours ouvrables selon votre localisation. Tout le matcha est expédié dans un emballage à température contrôlée pour garantir sa fraîcheur.",
      },
    },
    {
      id: "faq_006",
      q: {
        en: "What is the difference between a chawan and a regular bowl?",
        ja: "茶碗と普通の碗の違いは？",
        nl: "Wat is het verschil tussen een chawan en een gewone kom?",
        fr: "Quelle est la différence entre un chawan et un bol ordinaire ?",
      },
      a: {
        en: "A chawan is specifically designed for the tea ceremony. Its shape, weight, and glaze are all considered for the tactile experience of holding, whisking, and drinking. Each chawan is unique, embodying the wabi-sabi philosophy of finding beauty in imperfection. The wider opening allows proper whisking technique.",
        ja: "茶碗は茶の湯のために特別に設計されています。形、重さ、釉薬はすべて、持つ、点てる、飲むという触覚的な体験のために考慮されています。各茶碗は唯一無二で、不完全さの中に美を見出す侘び寂びの哲学を体現しています。",
        nl: "Een chawan is speciaal ontworpen voor de theeceremonie. De vorm, het gewicht en het glazuur zijn allemaal afgestemd op de tactiele ervaring van vasthouden, kloppen en drinken. Elke chawan is uniek en belichaamt de wabi-sabi-filosofie van schoonheid vinden in onvolmaaktheid。デキシオの開発者",
        fr: "Un chawan est spécialement conçu pour la cérémonie du thé. Sa forme, son poids et son émail sont tous pensés pour l'expérience tactile de tenir, fouetter et boire. Chaque chawan est unique, incarnant la philosophie wabi-sabi de trouver la beauté dans l'imperfection. L'ouverture plus large permet une bonne technique de fouet.",
      },
    },
  ],
  socialLinks: {
    instagram: "https://www.instagram.com/moecha_japan/",
  },
  slogan: "Crafted with Intention. Shared with Passion.",
  wholesale: {
    hero: {
      label: "Wholesale Program",
      title: "Exclusive Grade Matcha",
      subtitle: "Partner with us for direct access to Japan's finest ceremonial matcha — stone-ground from first-harvest tencha leaves in Uji and Nishio.",
      image: "https://images.unsplash.com/photo-1766025066682-0d330ffc9e7a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxqYXBhbmVzZSUyMHRlYSUyMGZhcm0lMjBoYXJ2ZXN0JTIwZ3JlZW58ZW58MXx8fHwxNzcyOTEwNTkwfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    },
    description: {
      label: "Why partner with us",
      title: "The MOECHA Difference",
      text: "We work exclusively with multi-generational tea farming families in Japan's premier growing regions. Our matcha is never blended with lower grades — every gram meets our exacting standards for color, umami depth, and ceremonial quality.",
      i18n: {
        ja: {
          label: "なぜ私たちとパートナーになるか",
          title: "MOECHAの違い",
          text: "私たちは日本最優れた栽培地域の多世代の茶農家家族と独占的に協力しています。私たちの抹茶は、より低いグレードとブレンドされることはありません—すべてのグラムが色、旨味の深さ、そして儀式的な品質に対する私たちの厳格な基準を満たしています。",
        },
        nl: {
          label: "Waarom partneren met ons",
          title: "Het MOECHA Verschil",
          text: "We werken exclusief met multi-generatie theeboerenfamilies in Japan's top groeiregios. Onze matcha wordt nooit gemengd met lagere kwaliteitsgraden — elke gram voldoet aan onze streng gestelde eisen voor kleur, umami-diepte en ceremoniële kwaliteit.",
        },
        fr: {
          label: "Pourquoi devenir notre partenaire",
          title: "La différence MOECHA",
          text: "Nous travaillons exclusivement avec des familles de fermiers à thé multi-générationnelles dans les régions de croissance les plus prestigieuses du Japon. Notre matcha n'est jamais mélangé avec des grades inférieurs — chaque gram répond à nos normes strictes en matière de couleur, de profondeur d'umami et de qualité cérémoniale.",
        },
      },
    },
    showcase: {
      label: "Our Offering",
      title: "Genuine First-Harvest Matcha",
      features: [
        "Single-origin from Uji, Kyoto & Nishio, Aichi",
        "Shade-grown for 20+ days before harvest",
        "Stone-ground on traditional granite mills",
        "Vibrant emerald color with rich umami profile",
        "Available in 500g, 1kg, and bulk formats",
        "Custom private-label and white-label programs",
      ],
      image: "https://images.unsplash.com/photo-1704650312191-005ab02786f5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjBwb3dkZXIlMjBidWxrJTIwcHJlbWl1bSUyMHF1YWxpdHl8ZW58MXx8fHwxNzcyOTEwNTkyfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
      cta_text: "Request Samples",
      i18n: {
        ja: {
          label: "私たちの提供",
          title: "真実の初収穫抹茶",
          features: [
            "宇治、京都、愛知県のニシオからの単一原産地",
            "収穫前20日以上遮光栽培",
            "伝統的な花崗岩ミルで挽く",
            "豊かな旨味のプロファイルを持つ鮮やかな緑色",
            "500g、1kg、バルクフォーマットで利用可能",
            "カスタムプライベートラベルとホワイトラベルプログラム",
          ],
          image: "https://images.unsplash.com/photo-1704650312191-005ab02786f5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjBwb3dkZXIlMjBidWxrJTIwcHJlbWl1bSUyMHF1YWxpdHl8ZW58MXx8fHwxNzcyOTEwNTkyfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
          cta_text: "サンプルをリクエスト",
        },
        nl: {
          label: "Onze Aanbieding",
          title: "Echt Eerste Oogst Matcha",
          features: [
            "Eenheidsoorsprong uit Uji, Kyoto en Nishio, Aichi",
            "20+ dagen in de schaduw gekweekt voor de oogst",
            "Gegrind op traditionele granietmijnen",
            "Vibrante emeraaldkleur met een rijke umami-profiel",
            "Beschikbaar in 500g, 1kg en bulkformaten",
            "Aangepaste private-label en white-labelprogramma's",
          ],
          image: "https://images.unsplash.com/photo-1704650312191-005ab02786f5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjBwb3dkZXIlMjBidWxrJTIwcHJlbWl1bSUyMHF1YWxpdHl8ZW58MXx8fHwxNzcyOTEwNTkyfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
          cta_text: "Samples Aanvragen",
        },
        fr: {
          label: "Notre Offre",
          title: "Matcha de Première Récolte Authentique",
          features: [
            "Origine unique d'Uji, Kyoto et Nishio, Aichi",
            "Cultivé à l'ombre pendant 20+ jours avant la récolte",
            "Moulé sur des meules granitiques traditionnelles",
            "Couleur émeraude vibrante avec un profil d'umami riche",
            "Disponible en formats 500g, 1kg et en gros",
            "Programmes de label privé et blanc personnalisés",
          ],
          image: "https://images.unsplash.com/photo-1704650312191-005ab02786f5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYXRjaGElMjBwb3dkZXIlMjBidWxrJTIwcHJlbWl1bSUyMHF1YWxpdHl8ZW58MXx8fHwxNzcyOTEwNTkyfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
          cta_text: "Demander des Échantillons",
        },
      },
    },
    tiers: [
      {
        name: "Artisan",
        minOrder: "5 kg",
        priceRange: "\u20ac180 / kg",
        features: [
          "Ceremonial grade matcha",
          "Custom packaging available",
          "Quarterly deliveries",
          "Dedicated account manager",
        ],
        i18n: {
          ja: {
            name: "アーティサン",
            minOrder: "5 kg",
            priceRange: "180ユーロ/キログラム",
            features: [
              "儀式的なグレードの抹茶",
              "カスタムパッケージングが利用可能",
              "四半期ご配達",
              "専任アカウントマネージャー",
            ],
          },
          nl: {
            name: "Artisan",
            minOrder: "5 kg",
            priceRange: "180 euro/kg",
            features: [
              "Ceremoniële kwaliteitsgraden matcha",
              "Aangepaste verpakking beschikbaar",
              "Kwartaalverzendingen",
              "Toegewijde accountmanager",
            ],
          },
          fr: {
            name: "Artisan",
            minOrder: "5 kg",
            priceRange: "180 euros/kg",
            features: [
              "Matcha de qualité cérémoniale",
              "Emballage personnalisé disponible",
              "Livraisons trimestrielles",
              "Gestionnaire de compte dédié",
            ],
          },
        },
      },
      {
        name: "Studio",
        minOrder: "25 kg",
        priceRange: "\u20ac145 / kg",
        features: [
          "Everything in Artisan",
          "Private label options",
          "Monthly deliveries",
          "Priority sourcing",
          "Marketing collateral",
        ],
        featured: true,
        i18n: {
          ja: {
            name: "スタジオ",
            minOrder: "25 kg",
            priceRange: "145ユーロ/キログラム",
            features: [
              "アーティサンのすべて",
              "プライベートラベルオプション",
              "月ご配達",
              "優先調達",
              "マーケティング資料",
            ],
          },
          nl: {
            name: "Studio",
            minOrder: "25 kg",
            priceRange: "145 euro/kg",
            features: [
              "Alles van Artisan",
              "Opties voor private label",
              "Maandelijkse verzendingen",
              "Prioritaire inkoop",
              "Marketingmaterialen",
            ],
          },
          fr: {
            name: "Studio",
            minOrder: "25 kg",
            priceRange: "145 euros/kg",
            features: [
              "Tout ce qui est dans Artisan",
              "Options de label privé",
              "Livraisons mensuelles",
              "Approvisionnement prioritaire",
              "Matériel marketing",
            ],
          },
        },
      },
      {
        name: "Enterprise",
        minOrder: "100 kg+",
        priceRange: "Custom",
        features: [
          "Everything in Studio",
          "Exclusive single-origin lots",
          "Weekly deliveries",
          "Custom grading & blending",
          "On-site farm visits",
          "Co-branding partnerships",
        ],
        i18n: {
          ja: {
            name: "エンタープライズ",
            minOrder: "100 kg以上",
            priceRange: "カスタム",
            features: [
              "スタジオのすべて",
              "排他的な単一原産地のロット",
              "週ご配達",
              "カスタムグレーディングとブレンド",
              "現場の農場訪問",
              "コブランドパートナーシップ",
            ],
          },
          nl: {
            name: "Enterprise",
            minOrder: "100 kg+",
            priceRange: "Aangepast",
            features: [
              "Alles van Studio",
              "Exclusieve single-origin loten",
              "Wekelijkse verzendingen",
              "Aangepaste gradatie en menging",
              "Bezoek aan de boerderijen",
              "Co-brandingpartnerschappen",
            ],
          },
          fr: {
            name: "Enterprise",
            minOrder: "100 kg+",
            priceRange: "Custom",
            features: [
              "Tout ce qui est dans Studio",
              "Lots exclusifs d'origine unique",
              "Livraisons hebdomadaires",
              "Grading et mélange personnalisés",
              "Visites sur les fermes",
              "Partenariats de co-marque",
            ],
          },
        },
      },
    ],
    faqs: [
      { q: "What grades of matcha do you offer for wholesale?", a: "We exclusively supply ceremonial and premium ceremonial grade matcha, sourced from the first harvest (ichibancha) of shade-grown tencha leaves. We do not carry culinary or blended grades." },
      { q: "What are the minimum order quantities?", a: "Our Artisan tier starts at 5 kg per order, Studio at 25 kg, and Enterprise at 100 kg+. We offer flexible scheduling depending on your needs." },
      { q: "Can I request samples before committing?", a: "Absolutely. We provide complimentary sample kits (3 \u00d7 30g tins) of our current harvest to qualified wholesale partners." },
      { q: "Do you offer private label or custom packaging?", a: "Yes. Our Studio and Enterprise tiers include private label and white-label options. Minimum order for custom packaging is 25 kg." },
      { q: "Where is your matcha sourced from?", a: "We partner directly with multi-generational tea farming families in Uji (Kyoto) and Nishio (Aichi). Every batch is single-origin, never blended across farms." },
      { q: "What certifications does your matcha carry?", a: "Our matcha is JAS Organic and USDA Organic certified. Every batch undergoes independent lab testing. Certificates of analysis are provided with each shipment." },
      { q: "How is pricing structured?", a: "Pricing is based on volume tier and commitment length. Artisan starts at \u20ac180/kg, Studio at \u20ac145/kg, and Enterprise pricing is custom. Annual contracts receive additional volume discounts." },
      { q: "What is the lead time for orders?", a: "Standard orders ship within 5\u20137 business days from our Kyoto warehouse. For large or custom orders, lead time is typically 2\u20133 weeks." },
    ],
    inquiry: {
      label: "Get in Touch",
      title: "Wholesale Inquiry",
      subtitle: "Tell us about your business and we'll craft a partnership tailored to your needs.",
      i18n: {
        ja: {
          label: "連絡ください",
          title: "卸売問い合わせ",
          subtitle: "あなたのビジネスについて教えてください。私たちはあなたのニーズに合わせたパートナーシップを作ります。",
        },
        nl: {
          label: "Neem Contact Op",
          title: "Wholesale Inquery",
          subtitle: "Vertel ons over uw bedrijf en we zullen een partnership creëren die aansluit bij uw behoeften.",
        },
        fr: {
          label: "Contactez-Nous",
          title: "Demande de Vente en Gros",
          subtitle: "Dites-nous à propos de votre entreprise et nous créerons une partenariat adapté à vos besoins.",
        },
      },
    },
    certifications: [
      { title: "Organic Certified", desc: "JAS & USDA organic certified farms" },
      { title: "Top 1% Grade", desc: "Only the first harvest, stone-ground" },
      { title: "Direct Trade", desc: "Sourced directly from Uji & Nishio farms" },
      { title: "Lab Tested", desc: "Every batch tested for purity & potency" },
    ],
  },
};

const defaultUsers: AdminUser[] = [
  {
    id: "usr_001",
    email: "admin@moecha.jp",
    name: "Kenji Tanaka",
    role: "admin",
    created_at: "2026-01-01T00:00:00Z",
    last_login: "2026-03-07T09:00:00Z",
  },
];

const defaultJournal: JournalEntry[] = [
  {
    id: 1,
    title: "The Origins of Uji Matcha",
    slug: "origins-of-uji-matcha",
    excerpt: "A journey through centuries of matcha cultivation in Japan's most revered tea region.",
    content: "The story of Uji matcha begins in the 12th century, when Buddhist monks brought tea seeds from China...",
    image: "https://images.unsplash.com/photo-1765035058192-edd7ee3e0df0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxqYXBhbmVzZSUyMHRlYSUyMGNlcmVtb255JTIwcml0dWFsfGVufDF8fHx8MTc3MjkwOTg1M3ww&ixlib=rb-4.1.0&q=80&w=1080",
    author: "Kenji Tanaka",
    published: true,
    created_at: "2026-02-15T10:00:00Z",
  },
];

const defaultReviews: Review[] = [
  {
    id: "rev_001",
    product_id: 1,
    name: "Alice Johnson",
    rating: 5,
    comment: "Absolutely love the ceremonial matcha! The flavor is rich and the texture is perfect for usucha.",
    approved: true,
    created_at: "2026-03-10T14:00:00Z",
  },
  {
    id: "rev_002",
    product_id: 2,
    name: "Bob Smith",
    rating: 4,
    comment: "The chawan bowl is beautiful and functional. Highly recommend for tea enthusiasts.",
    approved: true,
    created_at: "2026-03-15T10:00:00Z",
  },
];

const defaultPromoCodes: PromoCode[] = [
  {
    id: "promo_001",
    code: "MATCHA20",
    discount_type: "percentage",
    discount_value: 20,
    active: true,
    expires_at: "2026-12-31T23:59:59Z",
  },
  {
    id: "promo_002",
    code: "FREEBOWL",
    discount_type: "fixed",
    discount_value: 78,
    active: true,
    expires_at: "2026-12-31T23:59:59Z",
  },
];

const STORAGE_KEY = "moecha_admin_data";

/** Map DB row to JournalEntry (id is number; created_at as ISO string) */
function mapRowToJournalEntry(row: {
  id: number;
  title: string;
  slug: string;
  excerpt?: string | null;
  content?: string | null;
  image?: string | null;
  author?: string | null;
  published?: boolean;
  i18n?: Record<string, unknown> | null;
  created_at?: string | null;
}): JournalEntry {
  return {
    id: row.id,
    title: row.title,
    slug: row.slug,
    excerpt: row.excerpt ?? "",
    content: row.content ?? "",
    image: row.image ?? "",
    author: row.author ?? "",
    published: row.published ?? false,
    i18n: (row.i18n as I18nMap) ?? {},
    created_at: row.created_at ?? new Date().toISOString(),
  };
}

/** Map DB row to AdminWorkshop */
function mapRowToWorkshop(row: {
  id: number;
  title: string;
  date: string;
  location: string;
  image?: string | null;
  description?: string | null;
  i18n?: Record<string, unknown> | null;
}): AdminWorkshop {
  return {
    id: row.id,
    title: row.title,
    date: row.date,
    location: row.location,
    image: row.image ?? "",
    description: row.description ?? undefined,
    i18n: (row.i18n as I18nMap) ?? {},
  };
}

/** Map DB row to AdminProduct */
function mapRowToProduct(row: {
  id: number;
  slug: string;
  name: string;
  price: number;
  category: string;
  tag?: string | null;
  description?: string | null;
  details?: unknown;
  images?: string[] | null;
  related_ids?: number[] | null;
  stock_quantity?: number;
  low_stock_threshold?: number;
  i18n?: Record<string, unknown> | null;
}): AdminProduct {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    price: Number(row.price),
    category: row.category,
    tag: (row.tag as AdminProduct["tag"]) ?? "",
    description: row.description ?? "",
    details: Array.isArray(row.details) ? (row.details as { label: string; value: string }[]) : [],
    images: Array.isArray(row.images) ? row.images : [],
    relatedIds: Array.isArray(row.related_ids) ? row.related_ids : [],
    stock_quantity: row.stock_quantity ?? 0,
    low_stock_threshold: row.low_stock_threshold ?? 5,
    i18n: (row.i18n as I18nMap) ?? {},
  };
}

/** Map DB row to Review (id as string) */
function mapRowToReview(row: {
  id: number;
  product_id: number;
  name: string;
  rating: number;
  comment?: string | null;
  approved?: boolean;
  created_at?: string | null;
}): Review {
  return {
    id: String(row.id),
    product_id: row.product_id,
    name: row.name,
    rating: row.rating,
    comment: row.comment ?? "",
    approved: row.approved ?? false,
    created_at: row.created_at ?? new Date().toISOString(),
  };
}

/** Map DB row to PromoCode (id as string) */
function mapRowToPromoCode(row: {
  id: number;
  code: string;
  discount_type: string;
  discount_value: number;
  active?: boolean;
  expires_at?: string | null;
}): PromoCode {
  return {
    id: String(row.id),
    code: row.code,
    discount_type: row.discount_type as "percentage" | "fixed",
    discount_value: Number(row.discount_value),
    active: row.active ?? true,
    expires_at: row.expires_at ?? undefined,
  };
}

/** Map DB order (with items) to AdminOrder */
function mapRowToOrder(row: {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string | null;
  customer_country?: string | null;
  delivery: string;
  shipping_address?: string | null;
  notes?: string | null;
  status: string;
  subtotal: number;
  created_at?: string | null;
  items?: { product_id: number; product_name: string; quantity: number; unit_price: number }[];
}): AdminOrder {
  return {
    id: String(row.id),
    order_number: row.order_number,
    customer_name: row.customer_name,
    customer_email: row.customer_email,
    customer_phone: row.customer_phone ?? "",
    customer_country: row.customer_country ?? undefined,
    delivery: row.delivery as "pickup" | "shipping",
    shipping_address: row.shipping_address ?? "",
    notes: row.notes ?? "",
    subtotal: Number(row.subtotal),
    status: row.status as AdminOrder["status"],
    items: Array.isArray(row.items) ? row.items : [],
    created_at: row.created_at ?? new Date().toISOString(),
  };
}

function loadFromStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}_${key}`);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    console.warn(`[MOECHA] Failed to load "${key}" from storage:`, e);
    return fallback;
  }
}

/**
 * Merge stored siteConfig with defaults so that newly added keys
 * (e.g. wholesale) are seeded even when localStorage has an older snapshot.
 */
function loadSiteConfig(): SiteConfig {
  const stored = loadFromStorage<SiteConfig>("siteConfig", defaultSiteConfig);
  return {
    ...defaultSiteConfig,
    ...stored,
    hero: { ...defaultSiteConfig.hero, ...stored.hero },
    about: { ...defaultSiteConfig.about, ...stored.about },
    features: { ...defaultSiteConfig.features, ...stored.features },
    theme: stored.theme
      ? {
          colors: { ...defaultSiteConfig.theme!.colors, ...stored.theme.colors },
          navbar: { ...defaultSiteConfig.theme!.navbar, ...stored.theme.navbar },
          buttons: { ...defaultSiteConfig.theme!.buttons, ...stored.theme.buttons },
          fonts: { ...defaultSiteConfig.theme!.fonts, ...stored.theme.fonts },
        }
      : defaultSiteConfig.theme,
    wholesale: stored.wholesale
      ? {
          ...defaultSiteConfig.wholesale!,
          ...stored.wholesale,
          hero: { ...defaultSiteConfig.wholesale!.hero, ...stored.wholesale.hero },
          description: { ...defaultSiteConfig.wholesale!.description, ...stored.wholesale.description },
          showcase: { ...defaultSiteConfig.wholesale!.showcase, ...stored.wholesale.showcase },
          inquiry: { ...defaultSiteConfig.wholesale!.inquiry, ...stored.wholesale.inquiry },
        }
      : defaultSiteConfig.wholesale,
  };
}

// Track whether we've already shown the storage-full warning to avoid spamming
let storageWarningShown = false;

function saveToStorage(key: string, data: unknown) {
  try {
    localStorage.setItem(`${STORAGE_KEY}_${key}`, JSON.stringify(data));
  } catch (e) {
    console.warn(`[MOECHA] Storage save failed for "${key}":`, e);
    if (!storageWarningShown) {
      storageWarningShown = true;
      // Dynamically import to avoid circular deps — toast may not be available in all contexts
      import("sonner").then(({ toast }) => {
        toast.error("Storage is full. Recent changes may not be saved. Consider exporting data.");
      }).catch(() => {});
      setTimeout(() => { storageWarningShown = false; }, 30000);
    }
  }
}

// ─── Context ───
interface AdminDataContextType {
  products: AdminProduct[];
  setProducts: (p: AdminProduct[]) => void;
  addProduct: (p: AdminProduct) => Promise<{ error?: string }>;
  updateProduct: (p: AdminProduct) => Promise<{ error?: string }>;
  deleteProduct: (id: number) => Promise<{ error?: string }>;
  getNextProductId: () => number;
  productsLoading: boolean;

  orders: AdminOrder[];
  setOrders: (o: AdminOrder[]) => void;
  addOrder: (o: AdminOrder) => void;
  updateOrderStatus: (id: string, status: AdminOrder["status"]) => Promise<{ error?: string }>;
  updateOrderStatusBulk: (ids: string[], status: AdminOrder["status"]) => Promise<{ error?: string }>;
  hideOrders: (ids: string[]) => Promise<{ error?: string }>;
  ordersLoading: boolean;

  workshops: AdminWorkshop[];
  setWorkshops: (w: AdminWorkshop[]) => void;
  addWorkshop: (w: AdminWorkshop) => Promise<{ error?: string }>;
  updateWorkshop: (w: AdminWorkshop) => Promise<{ error?: string }>;
  deleteWorkshop: (id: number) => Promise<{ error?: string }>;
  getNextWorkshopId: () => number;
  workshopsLoading: boolean;

  siteConfig: SiteConfig;
  setSiteConfig: (c: SiteConfig) => void;

  users: AdminUser[];
  setUsers: (u: AdminUser[]) => void;
  addUser: (u: AdminUser) => void;
  updateUser: (u: AdminUser) => void;
  deleteUser: (id: string) => void;

  journal: JournalEntry[];
  setJournal: (j: JournalEntry[]) => void;
  addJournalEntry: (j: JournalEntry) => Promise<{ error?: string }>;
  updateJournalEntry: (j: JournalEntry) => Promise<{ error?: string }>;
  deleteJournalEntry: (id: number) => Promise<{ error?: string }>;
  getNextJournalId: () => number;
  journalLoading: boolean;

  reviews: Review[];
  setReviews: (r: Review[]) => void;
  addReview: (r: Review) => void;
  updateReview: (r: Review) => Promise<{ error?: string }>;
  deleteReview: (id: string) => Promise<{ error?: string }>;
  getNextReviewId: () => string;
  reviewsLoading: boolean;

  promoCodes: PromoCode[];
  setPromoCodes: (p: PromoCode[]) => void;
  addPromoCode: (p: PromoCode) => Promise<{ error?: string }>;
  updatePromoCode: (p: PromoCode) => Promise<{ error?: string }>;
  deletePromoCode: (id: string) => Promise<{ error?: string }>;
  getNextPromoCodeId: () => string;
  promoCodesLoading: boolean;

  categories: string[];
}

const AdminDataContext = createContext<AdminDataContextType | null>(null);

export function AdminDataProvider({ children }: { children: ReactNode }) {
  const [products, setProductsState] = useState<AdminProduct[]>(() => loadFromStorage("products", defaultProducts));
  const [productsLoading, setProductsLoading] = useState(false);
  const [orders, setOrdersState] = useState<AdminOrder[]>(() => loadFromStorage("orders", []));
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [workshops, setWorkshopsState] = useState<AdminWorkshop[]>(() => loadFromStorage("workshops", defaultWorkshops));
  const [workshopsLoading, setWorkshopsLoading] = useState(false);
  const [siteConfig, setSiteConfigState] = useState<SiteConfig>(() => loadSiteConfig());
  const [users, setUsersState] = useState<AdminUser[]>(() => loadFromStorage("users", defaultUsers));
  const [journal, setJournalState] = useState<JournalEntry[]>(() => loadFromStorage("journal", defaultJournal));
  const [journalLoading, setJournalLoading] = useState(false);
  const [reviews, setReviewsState] = useState<Review[]>(() => loadFromStorage("reviews", defaultReviews));
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [promoCodes, setPromoCodesState] = useState<PromoCode[]>(() => loadFromStorage("promoCodes", defaultPromoCodes));
  const [promoCodesLoading, setPromoCodesLoading] = useState(false);

  // Load journal from Supabase when connected (source of truth)
  useEffect(() => {
    if (!isSupabaseConnected()) return;
    setJournalLoading(true);
    fetchAllJournalEntries()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[MOECHA] Failed to fetch journal from Supabase:", error.message);
          return;
        }
        if (data && Array.isArray(data)) {
          setJournalState(data.map(mapRowToJournalEntry));
        }
      })
      .finally(() => setJournalLoading(false));
  }, []);

  // Load workshops from Supabase when connected (source of truth)
  useEffect(() => {
    if (!isSupabaseConnected()) return;
    setWorkshopsLoading(true);
    fetchWorkshops()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[MOECHA] Failed to fetch workshops from Supabase:", error.message);
          return;
        }
        if (data && Array.isArray(data)) {
          setWorkshopsState(data.map(mapRowToWorkshop));
        }
      })
      .finally(() => setWorkshopsLoading(false));
  }, []);

  // Load products from Supabase when connected
  useEffect(() => {
    if (!isSupabaseConnected()) return;
    setProductsLoading(true);
    fetchProducts()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[MOECHA] Failed to fetch products from Supabase:", error.message);
          return;
        }
        if (data && Array.isArray(data)) {
          setProductsState(data.map(mapRowToProduct));
        }
      })
      .finally(() => setProductsLoading(false));
  }, []);

  // Load site config from Supabase when connected (merge flat row into defaults)
  useEffect(() => {
    if (!isSupabaseConnected()) return;
    fetchSiteConfig()
      .then(({ data, error }) => {
        if (error || !data) return;
        const flat = flatToSiteConfig(data as Record<string, unknown>);
        setSiteConfigState((prev) => ({
          ...prev,
          ...flat,
          hero: { ...prev.hero, ...(flat.hero as object) },
          about: { ...prev.about, ...(flat.about as object) },
          features: { ...prev.features, ...(flat.features as object) },
          logo: (flat.logo as string) ?? prev.logo,
        }));
      });
  }, []);

  // Load reviews from Supabase when connected
  useEffect(() => {
    if (!isSupabaseConnected()) return;
    setReviewsLoading(true);
    fetchAllReviews()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[MOECHA] Failed to fetch reviews from Supabase:", error.message);
          return;
        }
        if (data && Array.isArray(data)) {
          setReviewsState(data.map(mapRowToReview));
        }
      })
      .finally(() => setReviewsLoading(false));
  }, []);

  // Load promo codes from Supabase when connected
  useEffect(() => {
    if (!isSupabaseConnected()) return;
    setPromoCodesLoading(true);
    fetchPromoCodes()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[MOECHA] Failed to fetch promo codes from Supabase:", error.message);
          return;
        }
        if (data && Array.isArray(data)) {
          setPromoCodesState(data.map(mapRowToPromoCode));
        }
      })
      .finally(() => setPromoCodesLoading(false));
  }, []);

  // Load orders from Supabase when connected
  useEffect(() => {
    if (!isSupabaseConnected()) return;
    setOrdersLoading(true);
    fetchOrders()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[MOECHA] Failed to fetch orders from Supabase:", error.message);
          return;
        }
        if (data && Array.isArray(data)) {
          setOrdersState(data.map(mapRowToOrder));
        }
      })
      .finally(() => setOrdersLoading(false));
  }, []);

  // Persist on change (only when Supabase not connected, so we don't overwrite DB data with localStorage)
  useEffect(() => {
    if (!isSupabaseConnected()) saveToStorage("products", products);
  }, [products]);
  useEffect(() => {
    if (!isSupabaseConnected()) saveToStorage("orders", orders);
  }, [orders]);
  useEffect(() => {
    if (!isSupabaseConnected()) saveToStorage("workshops", workshops);
  }, [workshops]);
  useEffect(() => { saveToStorage("siteConfig", siteConfig); }, [siteConfig]);
  useEffect(() => { saveToStorage("users", users); }, [users]);
  useEffect(() => {
    if (!isSupabaseConnected()) saveToStorage("journal", journal);
  }, [journal]);
  useEffect(() => {
    if (!isSupabaseConnected()) saveToStorage("reviews", reviews);
  }, [reviews]);
  useEffect(() => {
    if (!isSupabaseConnected()) saveToStorage("promoCodes", promoCodes);
  }, [promoCodes]);

  const categories = [...new Set(products.map((p) => p.category))];

  const setProducts = (p: AdminProduct[]) => setProductsState(p);

  const addProduct = useCallback(
    async (p: AdminProduct): Promise<{ error?: string }> => {
      if (isSupabaseConnected()) {
        const { data, error } = await insertProduct({
          slug: p.slug,
          name: p.name,
          price: p.price,
          category: p.category,
          tag: p.tag || null,
          description: p.description || null,
          details: p.details?.length ? p.details : null,
          images: p.images,
          related_ids: p.relatedIds,
          stock_quantity: p.stock_quantity,
          low_stock_threshold: p.low_stock_threshold,
          i18n: p.i18n as Record<string, unknown>,
        });
        if (error) return { error: error.message };
        if (data) setProductsState((prev) => [...prev, mapRowToProduct(data)]);
        return {};
      }
      setProductsState((prev) => [...prev, p]);
      return {};
    },
    []
  );

  const updateProduct = useCallback(
    async (p: AdminProduct): Promise<{ error?: string }> => {
      if (isSupabaseConnected()) {
        const { error } = await updateProductApi(p.id, {
          slug: p.slug,
          name: p.name,
          price: p.price,
          category: p.category,
          tag: p.tag || null,
          description: p.description || null,
          details: p.details,
          images: p.images,
          related_ids: p.relatedIds,
          stock_quantity: p.stock_quantity,
          low_stock_threshold: p.low_stock_threshold,
          i18n: p.i18n as Record<string, unknown>,
        });
        if (error) return { error: error.message };
        setProductsState((prev) => prev.map((x) => (x.id === p.id ? p : x)));
        return {};
      }
      setProductsState((prev) => prev.map((x) => (x.id === p.id ? p : x)));
      return {};
    },
    []
  );

  const deleteProduct = useCallback(
    async (id: number): Promise<{ error?: string }> => {
      if (isSupabaseConnected()) {
        const { error } = await deleteProductApi(id);
        if (error) return { error: error.message };
        setProductsState((prev) => prev.filter((x) => x.id !== id));
        return {};
      }
      setProductsState((prev) => prev.filter((x) => x.id !== id));
      return {};
    },
    []
  );

  const getNextProductId = () => Math.max(0, ...products.map((p) => p.id)) + 1;

  const setOrders = (o: AdminOrder[]) => setOrdersState(o);
  const addOrder = (o: AdminOrder) => setOrdersState((prev) => [o, ...prev]);

  const updateOrderStatus = useCallback(
    async (id: string, status: AdminOrder["status"]): Promise<{ error?: string }> => {
      const numId = Number(id);
      if (isSupabaseConnected() && !Number.isNaN(numId)) {
        const { error } = await updateOrderStatusApi(numId, status);
        if (error) return { error: error.message };
        setOrdersState((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
        return {};
      }
      setOrdersState((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      return {};
    },
    []
  );

  const updateOrderStatusBulk = useCallback(
    async (ids: string[], status: AdminOrder["status"]): Promise<{ error?: string }> => {
      const numIds = ids.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
      if (numIds.length === 0) return {};
      if (isSupabaseConnected()) {
        const { error } = await updateOrderStatusBulkApi(numIds, status);
        if (error) return { error: error.message };
      }
      setOrdersState((prev) =>
        prev.map((o) => (numIds.includes(Number(o.id)) ? { ...o, status } : o))
      );
      return {};
    },
    []
  );

  const hideOrders = useCallback(async (ids: string[]): Promise<{ error?: string }> => {
    const numIds = ids.map((id) => Number(id)).filter((n) => !Number.isNaN(n));
    if (numIds.length === 0) return {};
    if (isSupabaseConnected()) {
      const { error } = await hideOrdersApi(numIds);
      if (error) return { error: error.message };
    }
    setOrdersState((prev) => prev.filter((o) => !numIds.includes(Number(o.id))));
    return {};
  }, []);

  const setWorkshops = (w: AdminWorkshop[]) => setWorkshopsState(w);

  const addWorkshop = useCallback(
    async (w: AdminWorkshop): Promise<{ error?: string }> => {
      if (isSupabaseConnected()) {
        const { data, error } = await insertWorkshop({
          title: w.title,
          date: w.date,
          location: w.location,
          image: w.image || undefined,
          description: w.description,
          i18n: w.i18n as Record<string, unknown> | undefined,
        });
        if (error) return { error: error.message };
        if (data) setWorkshopsState((prev) => [...prev, mapRowToWorkshop(data)]);
        return {};
      }
      setWorkshopsState((prev) => [...prev, w]);
      return {};
    },
    []
  );

  const updateWorkshop = useCallback(
    async (w: AdminWorkshop): Promise<{ error?: string }> => {
      if (isSupabaseConnected()) {
        const { error } = await updateWorkshopApi(w.id, {
          title: w.title,
          date: w.date,
          location: w.location,
          image: w.image || undefined,
          description: w.description,
          i18n: w.i18n as Record<string, unknown> | undefined,
        });
        if (error) return { error: error.message };
        setWorkshopsState((prev) => prev.map((x) => (x.id === w.id ? w : x)));
        return {};
      }
      setWorkshopsState((prev) => prev.map((x) => (x.id === w.id ? w : x)));
      return {};
    },
    []
  );

  const deleteWorkshop = useCallback(
    async (id: number): Promise<{ error?: string }> => {
      if (isSupabaseConnected()) {
        const { error } = await deleteWorkshopApi(id);
        if (error) return { error: error.message };
        setWorkshopsState((prev) => prev.filter((x) => x.id !== id));
        return {};
      }
      setWorkshopsState((prev) => prev.filter((x) => x.id !== id));
      return {};
    },
    []
  );

  const getNextWorkshopId = () => Math.max(0, ...workshops.map((w) => w.id)) + 1;

  const setSiteConfig = (c: SiteConfig) => setSiteConfigState(c);

  const setUsers = (u: AdminUser[]) => setUsersState(u);
  const addUser = (u: AdminUser) => setUsersState((prev) => [...prev, u]);
  const updateUser = (u: AdminUser) => setUsersState((prev) => prev.map((x) => (x.id === u.id ? u : x)));
  const deleteUser = (id: string) => setUsersState((prev) => prev.filter((x) => x.id !== id));

  const setJournal = (j: JournalEntry[]) => setJournalState(j);

  const addJournalEntry = useCallback(
    async (j: JournalEntry): Promise<{ error?: string }> => {
      if (isSupabaseConnected()) {
        const { data, error } = await insertJournalEntry({
          title: j.title,
          slug: j.slug,
          excerpt: j.excerpt || undefined,
          content: j.content || undefined,
          image: j.image || undefined,
          author: j.author || undefined,
          published: j.published,
          i18n: j.i18n as Record<string, unknown> | undefined,
        });
        if (error) return { error: error.message };
        if (data) setJournalState((prev) => [...prev, mapRowToJournalEntry(data)]);
        return {};
      }
      setJournalState((prev) => [...prev, j]);
      return {};
    },
    []
  );

  const updateJournalEntry = useCallback(
    async (j: JournalEntry): Promise<{ error?: string }> => {
      if (isSupabaseConnected()) {
        const { error } = await updateJournalEntryApi(j.id, {
          title: j.title,
          slug: j.slug,
          excerpt: j.excerpt || undefined,
          content: j.content || undefined,
          image: j.image || undefined,
          author: j.author || undefined,
          published: j.published,
          i18n: j.i18n as Record<string, unknown> | undefined,
        });
        if (error) return { error: error.message };
        setJournalState((prev) => prev.map((x) => (x.id === j.id ? j : x)));
        return {};
      }
      setJournalState((prev) => prev.map((x) => (x.id === j.id ? j : x)));
      return {};
    },
    []
  );

  const deleteJournalEntry = useCallback(
    async (id: number): Promise<{ error?: string }> => {
      if (isSupabaseConnected()) {
        const { error } = await deleteJournalEntryApi(id);
        if (error) return { error: error.message };
        setJournalState((prev) => prev.filter((x) => x.id !== id));
        return {};
      }
      setJournalState((prev) => prev.filter((x) => x.id !== id));
      return {};
    },
    []
  );

  const getNextJournalId = () => Math.max(0, ...journal.map((j) => j.id)) + 1;

  const setReviews = (r: Review[]) => setReviewsState(r);
  const addReview = (r: Review) => setReviewsState((prev) => [...prev, r]);

  const updateReview = useCallback(
    async (r: Review): Promise<{ error?: string }> => {
      const numId = Number(r.id);
      if (isSupabaseConnected() && !Number.isNaN(numId)) {
        const { error } = await updateReviewApi(numId, { approved: r.approved });
        if (error) return { error: error.message };
        setReviewsState((prev) => prev.map((x) => (x.id === r.id ? r : x)));
        return {};
      }
      setReviewsState((prev) => prev.map((x) => (x.id === r.id ? r : x)));
      return {};
    },
    []
  );

  const deleteReview = useCallback(
    async (id: string): Promise<{ error?: string }> => {
      const numId = Number(id);
      if (isSupabaseConnected() && !Number.isNaN(numId)) {
        const { error } = await deleteReviewApi(numId);
        if (error) return { error: error.message };
        setReviewsState((prev) => prev.filter((x) => x.id !== id));
        return {};
      }
      setReviewsState((prev) => prev.filter((x) => x.id !== id));
      return {};
    },
    []
  );

  const getNextReviewId = () => `rev_${Math.max(0, ...reviews.map((r) => parseInt(r.id.split("_")[1], 10) || 0)) + 1}`;

  const setPromoCodes = (p: PromoCode[]) => setPromoCodesState(p);

  const addPromoCode = useCallback(
    async (p: PromoCode): Promise<{ error?: string }> => {
      if (isSupabaseConnected()) {
        const { data, error } = await insertPromoCode({
          code: p.code,
          discount_type: p.discount_type,
          discount_value: p.discount_value,
          active: p.active,
          expires_at: p.expires_at ?? null,
        });
        if (error) return { error: error.message };
        if (data) setPromoCodesState((prev) => [...prev, mapRowToPromoCode(data)]);
        return {};
      }
      setPromoCodesState((prev) => [...prev, p]);
      return {};
    },
    []
  );

  const updatePromoCode = useCallback(
    async (p: PromoCode): Promise<{ error?: string }> => {
      const numId = Number(p.id);
      if (isSupabaseConnected() && !Number.isNaN(numId)) {
        const { error } = await updatePromoCodeApi(numId, {
          code: p.code,
          discount_type: p.discount_type,
          discount_value: p.discount_value,
          active: p.active,
          expires_at: p.expires_at,
        });
        if (error) return { error: error.message };
        setPromoCodesState((prev) => prev.map((x) => (x.id === p.id ? p : x)));
        return {};
      }
      setPromoCodesState((prev) => prev.map((x) => (x.id === p.id ? p : x)));
      return {};
    },
    []
  );

  const deletePromoCode = useCallback(
    async (id: string): Promise<{ error?: string }> => {
      const numId = Number(id);
      if (isSupabaseConnected() && !Number.isNaN(numId)) {
        const { error } = await deletePromoCodeApi(numId);
        if (error) return { error: error.message };
        setPromoCodesState((prev) => prev.filter((x) => x.id !== id));
        return {};
      }
      setPromoCodesState((prev) => prev.filter((x) => x.id !== id));
      return {};
    },
    []
  );

  const getNextPromoCodeId = () => `promo_${Math.max(0, ...promoCodes.map((p) => parseInt(p.id.split("_")[1], 10) || 0)) + 1}`;

  return (
    <AdminDataContext.Provider
      value={{
        products, setProducts, addProduct, updateProduct, deleteProduct, getNextProductId, productsLoading,
        orders, setOrders, addOrder, updateOrderStatus, updateOrderStatusBulk, hideOrders, ordersLoading,
        workshops, setWorkshops, addWorkshop, updateWorkshop, deleteWorkshop, getNextWorkshopId, workshopsLoading,
        siteConfig, setSiteConfig,
        users, setUsers, addUser, updateUser, deleteUser,
        journal, setJournal, addJournalEntry, updateJournalEntry, deleteJournalEntry, getNextJournalId, journalLoading,
        reviews, setReviews, addReview, updateReview, deleteReview, getNextReviewId, reviewsLoading,
        promoCodes, setPromoCodes, addPromoCode, updatePromoCode, deletePromoCode, getNextPromoCodeId, promoCodesLoading,
        categories,
      }}
    >
      {children}
    </AdminDataContext.Provider>
  );
}

const noop = () => {};

const fallbackContext: AdminDataContextType = {
  products: [...defaultProducts],
  setProducts: noop,
  addProduct: async () => ({}),
  updateProduct: async () => ({}),
  deleteProduct: async () => ({}),
  getNextProductId: () => 999,
  productsLoading: false,

  orders: [],
  setOrders: noop,
  addOrder: noop,
  updateOrderStatus: async () => ({}),
  updateOrderStatusBulk: async () => ({}),
  hideOrders: async () => ({}),
  ordersLoading: false,

  workshops: [...defaultWorkshops],
  setWorkshops: noop,
  addWorkshop: async () => ({}),
  updateWorkshop: async () => ({}),
  deleteWorkshop: async () => ({}),
  getNextWorkshopId: () => 999,
  workshopsLoading: false,

  siteConfig: { ...defaultSiteConfig },
  setSiteConfig: noop,

  users: [],
  setUsers: noop,
  addUser: noop,
  updateUser: noop,
  deleteUser: noop,

  journal: [...defaultJournal],
  setJournal: noop,
  addJournalEntry: async () => ({}),
  updateJournalEntry: async () => ({}),
  deleteJournalEntry: async () => ({}),
  getNextJournalId: () => 999,
  journalLoading: false,

  reviews: [],
  setReviews: noop,
  addReview: noop,
  updateReview: async () => ({}),
  deleteReview: async () => ({}),
  getNextReviewId: () => "rev_999",
  reviewsLoading: false,

  promoCodes: [],
  setPromoCodes: noop,
  addPromoCode: async () => ({}),
  updatePromoCode: async () => ({}),
  deletePromoCode: async () => ({}),
  getNextPromoCodeId: () => "promo_999",
  promoCodesLoading: false,

  categories: [],
};

export function useAdminData() {
  const ctx = useContext(AdminDataContext);
  if (!ctx) return fallbackContext;
  return ctx;
}